/**
 * The Crisis Desk — is the airport ready for the day something breaks, and
 * what happens in the first quarter of an hour when it does.
 *
 * A playbook is a catalogue entry: the trigger, who holds it, the first
 * actions, and what the platform must show for the airport to be ready for
 * it. Readiness is computed from the records — the emergency exercise
 * obligation, the open severity-5 alerts, the equipment out of service, the
 * capacity model's critical positions, the documents in the library — and
 * every gap names the record that would close it. The drill prices a
 * scenario on the capacity model and the rate card, and says which.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { Alert } from "./alerts";
import type { ClosureCost, CriticalReport } from "./capacity";
import type { Closure } from "./closures";
import type { ComplianceReport, Obligation } from "./compliance";
import type { RateCard, SourceRef } from "./decision";
import { t, tn } from "../i18n";
import { money } from "./intelligence";
import { findKpi, type Kpi } from "./kpis";
import type { DocumentRow } from "./library";

export interface Playbook {
  key: string;
  name: string;
  trigger: string;
  /** who holds the first quarter of an hour */
  holds: string[];
  firstQuarterHour: string[];
  /** what the platform must show for the airport to be ready */
  needs: Need[];
}

export interface Need {
  key: string;
  what: string;
  kind: "obligation" | "document" | "kpi" | "alerts" | "capacity" | "entered";
  /** how the record is found: a title fragment, a document tag, a KPI key */
  match: string;
}

export interface Readiness {
  need: Need;
  met: boolean;
  basis: string;
  sources: SourceRef[];
  /** how to close it, when not met */
  todo?: string;
}

export interface PlaybookState {
  playbook: Playbook;
  readiness: Readiness[];
  met: number;
  score: number;
  /** what the scenario would do today, priced where the platform can price it */
  drill: { label: string; value: string; sources: SourceRef[]; assumed?: boolean }[];
  drillNote: string;
}

export interface CrisisEntered {
  /** exercises run: date, playbook key, what was learned */
  exercises?: { at: string; playbook: string; lesson: string }[];
  /** duty roles and who fills them today */
  roles?: Record<string, string>;
  enteredBy?: string;
  enteredAt?: string;
}

export interface CrisisInputs {
  now: Date;
  siteCode: string;
  alerts: Alert[];
  compliance: ComplianceReport | null;
  documents: DocumentRow[];
  kpis: Kpi[];
  critical: CriticalReport | null;
  closureCosts: Record<string, ClosureCost | null>;
  closures: Closure[];
  rateCard: RateCard;
  entered: CrisisEntered;
}

export interface CrisisDesk {
  generatedAt: string;
  currency: string;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  playbooks: PlaybookState[];
  /** the drill to run this month: the least ready playbook */
  drillFirst: PlaybookState | null;
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

/* ── the catalogue ────────────────────────────────────────────────────── */

export const PLAYBOOKS: Playbook[] = [
  {
    key: "runway",
    name: "Runway closed without notice",
    trigger:
      "a disabled aircraft, a pavement failure, a FOD find that cannot be cleared, a friction reading below the limit in rain",
    holds: ["Duty operations manager", "Airside operations", "ATC liaison", "Airline liaison"],
    firstQuarterHour: [
      "Declare the closure to ATC and issue the NOTAM; log the time.",
      "Open the capacity model for the runway and read the movements in the next four hours.",
      "Stand up airline liaison: diversions, holds and the bussing plan for stranded movements.",
      "Dispatch the airside inspection and the pavements crew; open a work order from the alert.",
      "Brief the chief executive with the movements affected and the earliest reopening.",
    ],
    needs: [
      {
        key: "exercise",
        what: "the aerodrome emergency exercise is current",
        kind: "obligation",
        match: "emergency exercise",
      },
      { key: "friction", what: "no runway alert at severity 5 is open", kind: "alerts", match: "runway" },
      { key: "capacity", what: "the capacity model covers the next 90 days", kind: "capacity", match: "known" },
      {
        key: "plan",
        what: "the aerodrome emergency plan is issued in the library",
        kind: "document",
        match: "emergency",
      },
    ],
  },
  {
    key: "power",
    name: "Total loss of power",
    trigger: "a grid failure with the standby set failing to take the load",
    holds: ["Duty engineer", "Electrical crew", "Terminal operations", "Security"],
    firstQuarterHour: [
      "Confirm the standby set and the UPS on the AGL and the tower; start the second set.",
      "Hold departures until the AGL circuits are confirmed on standby power.",
      "Terminal: lifts, screening lanes and the baggage lines to manual procedure.",
      "Open the electrical serviceability round and log every circuit as it returns.",
    ],
    needs: [
      {
        key: "agl",
        what: "the weekly AGL night serviceability check is current",
        kind: "obligation",
        match: "AGL night serviceability",
      },
      { key: "outages", what: "major equipment out of service is within target", kind: "kpi", match: "outages" },
      {
        key: "plan",
        what: "the aerodrome emergency plan is issued in the library",
        kind: "document",
        match: "emergency",
      },
    ],
  },
  {
    key: "security",
    name: "Security breach or threat",
    trigger: "a perimeter breach, an unattended item, a credible threat to the terminal",
    holds: ["Duty security manager", "Police liaison", "Terminal operations", "Communications"],
    firstQuarterHour: [
      "Isolate the area and hold the screening lanes; log the time.",
      "Police and the security programme's chain of command informed.",
      "Passenger flow re-routed; the queue KPI watched live.",
      "One spokesperson, one statement, cleared by the chief executive.",
    ],
    needs: [
      { key: "incidents", what: "no security incident is open", kind: "kpi", match: "security" },
      {
        key: "programme",
        what: "the security program is issued in the library",
        kind: "document",
        match: "security",
      },
      {
        key: "exercise",
        what: "the aerodrome emergency exercise is current",
        kind: "obligation",
        match: "emergency exercise",
      },
    ],
  },
  {
    key: "weather",
    name: "Sandstorm or severe wind",
    trigger: "visibility below minima, wind above the aircraft limits, sand on the movement area",
    holds: ["Duty operations manager", "Airside operations", "Met liaison", "Airline liaison"],
    firstQuarterHour: [
      "Read the weather disruption KPI and the forecast window; brief the airlines.",
      "Sweepers staged; the FOD and friction rounds brought forward for the recovery.",
      "Ground handling equipment secured; stands checked for loose items.",
      "Passengers informed once, with a time to expect the next update.",
    ],
    needs: [
      { key: "weather", what: "weather disruption today is within target", kind: "kpi", match: "weather" },
      { key: "friction", what: "no runway alert at severity 5 is open", kind: "alerts", match: "runway" },
    ],
  },
  {
    key: "baggage",
    name: "Baggage system failure",
    trigger: "a line down at peak, a sortation fault, a screening integration failure",
    holds: ["Terminal operations", "Baggage engineering", "Handling agents", "Airline liaison"],
    firstQuarterHour: [
      "Manual sortation opened; handlers redeployed to it.",
      "Departures held only where bags cannot be reconciled.",
      "Engineering on the fault with the work order raised from the alert.",
      "Bags-on-time KPI watched; the airlines told which flights will go without bags.",
    ],
    needs: [
      { key: "bags", what: "bags delivered on time is within target", kind: "kpi", match: "baggage" },
      { key: "outages", what: "major equipment out of service is within target", kind: "kpi", match: "outages" },
    ],
  },
  {
    key: "medical",
    name: "Aircraft accident or mass casualty",
    trigger: "an aircraft accident on or near the aerodrome",
    holds: [
      "Chief executive",
      "Duty operations manager",
      "Rescue and fire fighting",
      "Medical",
      "Police and civil defence liaison",
      "Communications",
    ],
    firstQuarterHour: [
      "Rescue and fire fighting responding; the emergency plan's alerting cascade started.",
      "Runway and the movement area closed; ATC informed.",
      "Emergency operations centre opened; the chief executive in it.",
      "Family assistance and the media line stood up on the plan's wording.",
    ],
    needs: [
      {
        key: "exercise",
        what: "the aerodrome emergency exercise is current",
        kind: "obligation",
        match: "emergency exercise",
      },
      {
        key: "plan",
        what: "the aerodrome emergency plan is issued in the library",
        kind: "document",
        match: "emergency",
      },
      { key: "wildlife", what: "the wildlife hazard assessment is current", kind: "obligation", match: "wildlife" },
    ],
  },
];

/** A playbook's name for the screen, from its key; an unknown key is shown as it was entered. */
function playbookName(key: string): string {
  const p = PLAYBOOKS.find((x) => x.key === key);
  return p ? t(p.name) : key;
}

/* ── readiness from the records ───────────────────────────────────────── */

function obligationFor(comp: ComplianceReport | null, match: string): Obligation | undefined {
  const rows = comp?.obligations.filter((o) => o.title.toLowerCase().includes(match.toLowerCase())) ?? [];
  const rank = { overdue: 0, pending: 1, met: 2, waived: 3 } as const;
  return rows.sort((a, b) => rank[a.status] - rank[b.status] || (b.dueAt ?? "").localeCompare(a.dueAt ?? ""))[0];
}

function readiness(need: Need, inp: CrisisInputs): Readiness {
  switch (need.kind) {
    case "obligation": {
      const o = obligationFor(inp.compliance, need.match);
      if (!o)
        return {
          need,
          met: false,
          basis: t("no such obligation on the compliance register"),
          sources: [],
          todo: t("add the obligation to the register"),
        };
      const src: SourceRef = { kind: "obligation", ref: o.periodKey ?? o.title, label: o.title, href: "/compliance" };
      return {
        need,
        met: o.status === "met",
        basis: `${t("{title}: {status}", { title: o.title, status: t(o.status) })}${o.dueAt ? t(", due {date}", { date: o.dueAt.slice(0, 10) }) : ""}${o.metAt ? t(", met {date}", { date: o.metAt.slice(0, 10) }) : ""}`,
        sources: [src],
        todo:
          o.status === "met"
            ? undefined
            : o.status === "overdue"
              ? t("run it and file the evidence — it is overdue")
              : t("schedule it before it falls due"),
      };
    }
    case "document": {
      const docs = inp.documents.filter(
        (d) =>
          (d.title.toLowerCase().includes(need.match) || d.tags.some((t) => t.includes(need.match))) &&
          d.status === "issued",
      );
      const expired = docs.filter((d) => d.expiresOn && d.expiresOn < inp.now.toISOString().slice(0, 10));
      const live = docs.filter((d) => !expired.includes(d));
      return {
        need,
        met: live.length > 0,
        basis: live.length
          ? t("in the library: {titles}", { titles: live.map((d) => d.title).join("; ") })
          : expired.length
            ? t("expired: {titles}", { titles: expired.map((d) => `${d.title} (${d.expiresOn})`).join("; ") })
            : t('no issued document matching "{match}" in the library', { match: need.match }),
        sources: docs.map((d) => ({
          kind: "document",
          ref: d.reference ?? d.id,
          label: d.title,
          href: `/library/${d.id}`,
        })),
        todo: live.length ? undefined : t("issue the document in the library, tagged so the desk can find it"),
      };
    }
    case "kpi": {
      const k = findKpi(inp.kpis, need.match);
      if (!k)
        return {
          need,
          met: false,
          basis: t("no KPI {key} on the platform", { key: need.match }),
          sources: [],
          todo: t("connect the feed"),
        };
      const within =
        k.value != null && k.target != null && (k.direction === "down" ? k.value <= k.target : k.value >= k.target);
      return {
        need,
        met: within,
        basis: `${t("{label}: {value} {unit} against a target of {target}", { label: k.label, value: k.value ?? t("not computable"), unit: k.unit, target: k.target ?? "—" })}${k.reason ? ` (${k.reason})` : ""}`,
        sources: [{ kind: "kpi", ref: k.key, label: k.label, href: "/executive-kpis" }],
        todo: within ? undefined : k.value == null ? t("make the KPI computable") : t("bring the KPI within target"),
      };
    }
    case "alerts": {
      const open = inp.alerts.filter(
        (a) =>
          a.status === "open" && a.severity >= 5 && `${a.title} ${a.zoneCode ?? ""}`.toLowerCase().includes(need.match),
      );
      return {
        need,
        met: open.length === 0,
        basis: open.length
          ? t("open: {titles}", { titles: open.map((a) => a.title).join("; ") })
          : t("no severity-5 alert open"),
        sources: open.map((a) => ({ kind: "alert", ref: a.id, label: a.title, href: `/alerts/${a.id}` })),
        todo: open.length ? t("close the alert — the work is mandatory") : undefined,
      };
    }
    case "capacity": {
      const c = inp.critical;
      return {
        need,
        met: !!c?.known,
        basis: c?.known
          ? t("covered; {positions}: {list}", {
              positions: tn(c.critical.length, "{n} critical position", "{n} critical positions"),
              list: c.critical.map((p) => p.position).join(", ") || t("none"),
            })
          : t("not covered: {why}", { why: c?.whyNot ?? t("no capacity model") }),
        sources: c
          ? [{ kind: "capacity", ref: "critical positions", label: t("the next 90 days"), href: "/capacity" }]
          : [],
        todo: c?.known ? undefined : t("load the schedule so the model can price a closure"),
      };
    }
    default:
      return { need, met: false, basis: t("not assessed"), sources: [] };
  }
}

/* ── the drill ────────────────────────────────────────────────────────── */

function drill(p: Playbook, inp: CrisisInputs): { rows: PlaybookState["drill"]; note: string } {
  const cur = inp.rateCard.currency;
  const rows: PlaybookState["drill"] = [];
  const runwayZone = Object.keys(inp.closureCosts).find((z) => z.startsWith("RWY"));
  const cc = runwayZone ? inp.closureCosts[runwayZone] : null;
  const unplanned = inp.closures
    .filter((c) => c.kind === "unplanned" && c.minutes)
    .sort((a, b) => (b.minutes ?? 0) - (a.minutes ?? 0))[0];
  const hours = unplanned?.minutes ? Math.round((unplanned.minutes / 60) * 10) / 10 : 4;
  if (p.key === "runway" || p.key === "weather" || p.key === "medical") {
    rows.push({
      label: t("movements in the window"),
      value: cc?.known
        ? t("{movements} in {hours} h: {stranded} stranded, {bussed} bussed", {
            movements: cc.movements,
            hours: cc.hours ?? "—",
            stranded: cc.stranded,
            bussed: cc.bussed,
          })
        : t("unknown — the capacity model has no schedule"),
      sources: cc ? [{ kind: "capacity", ref: runwayZone!, label: t("closure cost"), href: "/capacity" }] : [],
    });
    rows.push({
      label: t("cost of {hours} h closed", { hours }),
      value: money(Math.round(hours * inp.rateCard.closureCostPerHour.runway), cur),
      assumed: true,
      sources: [
        { kind: "rate_card", ref: "rate card", label: t("closure cost per hour, runway") },
        ...(unplanned
          ? [
              {
                kind: "closure" as const,
                ref: unplanned.reference ?? unplanned.id,
                label: t("{minutes} min — the longest unplanned closure on record", {
                  minutes: unplanned.minutes ?? "",
                }),
                href: "/capacity",
              },
            ]
          : []),
      ],
    });
  }
  if (p.key === "power" || p.key === "baggage") {
    const outages = findKpi(inp.kpis, "outages");
    rows.push({
      label: t("equipment already out"),
      value: outages
        ? t("{value} {unit} before the event ({source})", {
            value: outages.value ?? "—",
            unit: outages.unit,
            source: outages.source ?? "KPI",
          })
        : t("no equipment KPI"),
      sources: outages ? [{ kind: "kpi", ref: "outages", label: outages.label, href: "/executive-kpis" }] : [],
    });
    const stand = inp.rateCard.closureCostPerHour.terminal;
    rows.push({
      label: t("terminal cost per hour"),
      value: t("{cost} on the rate card", { cost: money(stand, cur) }),
      assumed: true,
      sources: [{ kind: "rate_card", ref: "rate card", label: t("closure cost per hour, terminal") }],
    });
  }
  if (p.key === "security") {
    const queue = findKpi(inp.kpis, "queue");
    rows.push({
      label: t("worst queue today"),
      value: queue
        ? t("{value} {unit} against {target}", {
            value: queue.value ?? "—",
            unit: queue.unit,
            target: queue.target ?? "—",
          })
        : t("no queue KPI"),
      sources: queue ? [{ kind: "kpi", ref: "queue", label: queue.label, href: "/executive-kpis" }] : [],
    });
  }
  const crit = inp.critical;
  if (crit?.known && crit.critical.length)
    rows.push({
      label: t("positions that strand traffic"),
      value: crit.critical.map((c) => `${c.position} (${c.stranded})`).join(", "),
      sources: [{ kind: "capacity", ref: "critical positions", label: t("the next 90 days"), href: "/capacity" }],
    });
  return {
    rows,
    note: rows.length
      ? t("priced on the capacity model where it covers the window, else the rate card, and it says which")
      : t("the platform holds nothing to price this scenario with"),
  };
}

/* ── the desk ─────────────────────────────────────────────────────────── */

export function buildCrisisDesk(inp: CrisisInputs): CrisisDesk {
  const cur = inp.rateCard.currency;
  const playbooks: PlaybookState[] = PLAYBOOKS.map((p) => {
    const rd = p.needs.map((n) => readiness(n, inp));
    const met = rd.filter((r) => r.met).length;
    const d = drill(p, inp);
    return {
      playbook: p,
      readiness: rd,
      met,
      score: Math.round((100 * met) / Math.max(1, rd.length)),
      drill: d.rows,
      drillNote: d.note,
    };
  });
  const drillFirst = [...playbooks].sort((a, b) => a.score - b.score)[0] ?? null;
  const exercise = obligationFor(inp.compliance, "emergency exercise");
  const openS5 = inp.alerts.filter((a) => a.status === "open" && a.severity >= 5);
  const outages = findKpi(inp.kpis, "outages");
  const security = findKpi(inp.kpis, "security");
  const lastDrill = (inp.entered.exercises ?? []).sort((a, b) => b.at.localeCompare(a.at))[0];
  const gapsCount = playbooks.reduce((t, p) => t + p.readiness.filter((r) => !r.met).length, 0);
  const tiles: CrisisDesk["tiles"] = [
    {
      key: "exercise",
      label: t("emergency exercise"),
      value: null,
      unit: "",
      text: exercise
        ? `${t(exercise.status)}${exercise.dueAt ? t(", due {date}", { date: exercise.dueAt.slice(0, 10) }) : ""}`
        : t("not on the register"),
      tone: exercise?.status === "met" ? "good" : "bad",
      sources: exercise
        ? [
            {
              kind: "obligation",
              ref: exercise.periodKey ?? exercise.title,
              label: exercise.title,
              href: "/compliance",
            },
          ]
        : [],
    },
    {
      key: "s5",
      label: t("severity-5 alerts open"),
      value: openS5.length,
      unit: "",
      text: openS5.map((a) => a.title).join("; ") || t("none"),
      tone: openS5.length ? "bad" : "good",
      sources: openS5.map((a) => ({ kind: "alert", ref: a.id, label: a.title, href: `/alerts/${a.id}` })),
    },
    {
      key: "outages",
      label: t("major equipment out"),
      value: outages?.value ?? null,
      unit: outages?.unit ?? "",
      text: outages
        ? t("target {target}; {source}", { target: outages.target ?? "—", source: outages.source ?? "" })
        : t("no KPI"),
      tone:
        outages && outages.value != null && outages.target != null && outages.value > outages.target ? "warn" : "good",
      sources: outages ? [{ kind: "kpi", ref: "outages", label: outages.label, href: "/executive-kpis" }] : [],
    },
    {
      key: "security",
      label: t("security incidents open"),
      value: security?.value ?? null,
      unit: "",
      text: security ? t("target {target}", { target: security.target ?? "—" }) : t("no KPI"),
      tone: security && (security.value ?? 0) > 0 ? "bad" : "good",
      sources: security ? [{ kind: "kpi", ref: "security", label: security.label, href: "/executive-kpis" }] : [],
    },
    {
      key: "gaps",
      label: t("readiness gaps"),
      value: gapsCount,
      unit: "",
      text: t("across {n} playbooks; least ready: {name} at {score}%", {
        n: playbooks.length,
        name: drillFirst ? t(drillFirst.playbook.name) : "—",
        score: drillFirst?.score ?? "—",
      }),
      tone: gapsCount ? "warn" : "good",
      sources: [],
    },
    {
      key: "drill",
      label: t("last drill recorded"),
      value: null,
      unit: "",
      text: lastDrill ? `${lastDrill.at.slice(0, 10)} — ${playbookName(lastDrill.playbook)}` : t("none recorded here"),
      assumed: !!lastDrill,
      tone: lastDrill ? "good" : "warn",
      sources: lastDrill
        ? [{ kind: "entered", ref: "entered", label: t("drill log"), enteredBy: inp.entered.enteredBy }]
        : [],
    },
  ];
  const boardLine = [
    t("Crisis readiness at {site}: {gaps} across {n} playbooks; the emergency exercise is {status}.", {
      site: inp.siteCode,
      gaps: tn(gapsCount, "{n} gap", "{n} gaps"),
      n: playbooks.length,
      status: exercise ? t(exercise.status) : t("not on the register"),
    }),
    drillFirst
      ? t("The drill to run first is {name}: {met} of {total} needs met, {missing}.", {
          name: t(drillFirst.playbook.name).toLowerCase(),
          met: drillFirst.met,
          total: drillFirst.readiness.length,
          missing:
            drillFirst.readiness
              .filter((r) => !r.met)
              .map((r) => t(r.need.what))
              .join("; ") || t("nothing missing"),
        })
      : t("No playbooks."),
    openS5.length
      ? tn(
          openS5.length,
          "{n} severity-5 alert is open now — the first scenario is already half underway.",
          "{n} severity-5 alerts are open now — the first scenario is already half underway.",
        )
      : t("No severity-5 alert is open; the desk is about readiness, not response."),
  ];
  const material = materialForCrisis({ tiles, playbooks, boardLine } as CrisisDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    tiles,
    playbooks,
    drillFirst,
    boardLine,
    gaps: [
      ...(inp.compliance ? [] : [t("the compliance register is not on the platform")]),
      ...(inp.critical?.known ? [] : [t("the capacity model does not cover the next 90 days")]),
    ],
    figures,
  };
}

export const CRISIS_MODEL_PROMPT = [
  "You are advising an airport's chief executive on crisis readiness from the material given and nothing else.",
  "You may explain, prioritise and argue; you must not state any number that is not in the material.",
  "Say when a figure rests on a rate card or an entered value rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForCrisis(d: Pick<CrisisDesk, "tiles" | "playbooks" | "boardLine">): string {
  const L: string[] = [];
  for (const t of d.tiles)
    L.push(`Tile ${t.label}: ${t.value ?? "n/a"} ${t.unit} — ${t.text}${t.assumed ? " [entered]" : ""}`);
  for (const p of d.playbooks) {
    L.push(
      `Playbook ${p.playbook.key} "${p.playbook.name}": ${p.met} of ${p.readiness.length} needs met (${p.score}%). Trigger: ${p.playbook.trigger}. Holds: ${p.playbook.holds.join(", ")}.`,
    );
    for (const r of p.readiness)
      L.push(`  ${r.met ? "met" : "GAP"}: ${r.need.what} — ${r.basis}${r.todo ? ` — to do: ${r.todo}` : ""}`);
    for (const row of p.drill) L.push(`  drill ${row.label}: ${row.value}${row.assumed ? " [rate card]" : ""}`);
  }
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerCrisis(q: string, d: CrisisDesk): string | null {
  const t = q.toLowerCase();
  if (/first|drill|least ready|priorit/.test(t) && d.drillFirst)
    return `${d.drillFirst.playbook.name}: ${d.drillFirst.met} of ${d.drillFirst.readiness.length} needs met. Gaps: ${
      d.drillFirst.readiness
        .filter((r) => !r.met)
        .map((r) => `${r.need.what} (${r.todo ?? r.basis})`)
        .join("; ") || "none"
    }.`;
  if (/gap|missing|not ready/.test(t))
    return (
      d.playbooks
        .flatMap((p) => p.readiness.filter((r) => !r.met).map((r) => `${p.playbook.name}: ${r.need.what} — ${r.basis}`))
        .join("\n") || "No gaps: every need on every playbook is met."
    );
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  const p = d.playbooks.find(
    (x) => t.includes(x.playbook.key) || t.includes(x.playbook.name.toLowerCase().slice(0, 12)),
  );
  if (p)
    return `${p.playbook.name} (${p.score}% ready). Holds: ${p.playbook.holds.join(", ")}. First quarter hour: ${p.playbook.firstQuarterHour.join(" ")} Drill: ${p.drill.map((r) => `${r.label} ${r.value}`).join("; ") || "nothing to price"}.`;
  return null;
}
